import { useCallback, useEffect, useRef, useState, type CSSProperties, type JSX } from 'react'
import logoUrl from '../assets/aps-logo.svg'
import {
  api, ApiError, type CurrentConfig, type PresentationEstimate, type PresentationPayload,
} from '../api.js'
import {
  ConfidencePill, formatDate, formatHour, formatKw, formatMoney, KwFigure,
} from '../components/Pill.js'
import { ProfileChart } from '../components/ProfileChart.js'
import { T } from '../tokens.js'

/**
 * Presentation mode (R-28, spec §7.3) — the screen a rep turns the laptop
 * around to.
 *
 * ## The mechanism is the requirement
 *
 * **This screen fetches `?mode=presentation`. It does not filter the drill-down
 * payload.** Everything below rests on that one line: the only reading of
 * "absent" that survives a devtools inspection or a screen-share is that the
 * data never reached the browser. A component that receives notes, flags,
 * cohort percentiles and confidence reasons and declines to draw them **looks
 * identical on screen** and fails the requirement completely — which is exactly
 * why it would survive a review that only looked at the page.
 *
 * That is also why `Presentation.test.tsx` asserts on the **response the
 * component received** rather than on the DOM. The injection that proves those
 * tests can fail (point this at `api.account` and filter here) leaves the
 * rendered screen pixel-identical; the leak tests are the only thing that goes
 * red. A DOM assertion would have passed.
 *
 * The projection is the server's (`http/presentation.ts`, Task 4, hardened
 * after review so that every *leaf* is checked and not merely every field
 * name). This screen consumes it and goes nowhere near the internal route.
 *
 * ## What it draws, and what it cannot draw
 *
 * The design record's presentation view — the mock's own — is a kicker, the
 * customer variant of the chart, the binding-hour sentence, and a right rail of
 * recommended nomination, estimated season payment split into capacity and
 * energy, the assumptions line and the disclaimer. There is no characterization
 * card in it, no decomposition, no flags, no cohort position and no note, and
 * the payload carries none of those either. The two agree, and that agreement
 * is the design rather than a coincidence.
 *
 * **Display rule 1 holds here, and this is where dropping it is most
 * tempting.** The confidence label stays, beside the 62px figure rather than
 * across the screen from it, and it is spelled out — `Medium confidence`, the
 * one-pager's own wording — because the reader is not a rep and a bare chip
 * reads as a size band. What does *not* cross is the reasoning behind the
 * label: `confidence.reasons` ("held one level below what the data quality
 * alone would support") is reasoning about the program's own method, and the
 * server does not send it.
 *
 * ## The chrome changes visibly, in both directions
 *
 * Spec §7.3 asks for it, and the worst outcome it protects against is a rep who
 * thinks they are in presentation mode and is not. This is a full-viewport
 * overlay: the rainbow rule, the app bar, the navigation, the breadcrumb, the
 * rail, the flags banner and the what-if controls are all gone, replaced by an
 * APS mark and two enormous figures. It is not a mode a rep can be in by
 * accident, and `Exit presentation` is in the top right at every scroll
 * position. `document.body` is marked `data-mode="presentation"` and its
 * scrolling is locked while it is open, so nothing internal can be scrolled
 * into view behind it.
 *
 * ## A scenario is not presented here — see `Presentation`'s caller
 *
 * `AccountDetail` disables the entry control while a what-if is open. There is
 * no presentation payload for a scenario: the projection has no field for "not
 * the estimate of record" (which is why Task 8 made `?asOf` + `?mode` a 400),
 * a scenario's confidence label is **pre-cohort** and not comparable with the
 * record's, and building the screen from the scenario envelope the browser
 * already holds would be precisely the client-side filtering this file exists
 * to refuse. The customer-facing artifact for a scenario is the one-pager,
 * which is server-rendered, carries the scenario band and states the basis of
 * its label in words.
 */

/**
 * What Tab can land on. `[tabindex="-1"]` is excluded deliberately — the
 * overlay's own root carries it, and it is a focus *target*, not a focus stop.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)]
}

/** The kicker, the mock's, verbatim. */
const KICKER = 'What you could curtail, hour by hour'

const NOMINATION_LABEL = 'Recommended nomination'
const SEASON_LABEL = 'Estimated season payment'
const CAPACITY_LABEL = 'Capacity payment'
const ENERGY_LABEL = 'Energy payment'
const EXIT_LABEL = 'Exit presentation'

/**
 * The disclaimer, mandated verbatim. Two sentences and both of them load
 * bearing: the figure came from this customer's own meter, and it is not an
 * offer.
 */
const DISCLAIMER = "Computed from this account's own interval meter history. An estimate, not an offer."

/**
 * The mock's assumptions line, and where each number in it comes from.
 *
 * The mock names the season dates, the event count, the assumed duration and
 * both rates. **None of those is on the presentation payload**, deliberately:
 * Task 15 ruled that configuration earns a place on a customer-visible payload
 * only when a customer-visible sentence is *derived* from it, and that rule
 * still holds — nothing here asks the presentation route to carry more.
 *
 * They come instead from `api.config()` (`GET /api/config/current`), which is
 * the configuration in force today in projection, and every field of which the
 * one-pager already prints to customers in its `Assumptions this rests on`
 * block. So this sentence discloses nothing the customer's own document does
 * not, and the numbers are the program's, read at the moment the screen
 * opened.
 *
 * The alternative — writing `12 events at 4 hours, $12.00/kW-month` into this
 * package — is program configuration in a browser bundle, versioned by nobody,
 * and the exact mistake the mock's client-side confidence weights are. That
 * paragraph is why this fetches rather than hard-codes, and it is not licence
 * to inline the numbers the day the request is inconvenient.
 *
 * {@link ASSUMPTIONS_TAIL} is what the sentence falls back to when the
 * configuration did not arrive. A customer-facing screen must not print half a
 * sentence or a blank where a rate belongs, and the one-pager really does list
 * all six server-side, so pointing at it stays a true statement.
 */
const ASSUMPTIONS_TAIL = 'The season dates, the number of events assumed and their duration, and the '
  + 'capacity and energy rates are printed on the one-pager.'

/** `$12.00`, `$0.18` — two places for a rate, which is how both are quoted. */
function formatRate(value: number): string {
  return `$${value.toFixed(2)}`
}

function assumptionsTail(config: CurrentConfig | null): string {
  if (config === null) return ASSUMPTIONS_TAIL
  const { events, rates, season } = config
  return `The season runs ${formatDate(season.start)} to ${formatDate(season.end)}, and this assumes `
    + `${events.assumedCount} events of ${events.assumedDurationHours} hours, at `
    + `${formatRate(rates.demandPerKwMonth)} per kW-month of capacity and `
    + `${formatRate(rates.energyPerKwh)} per kWh of energy.`
}

/** Display rule 4: an account this run did not score is normal, not a gap. */
const NOTHING_TO_PRESENT = 'There is no estimate of record for this account on the current run, '
  + 'so there is nothing to show here.'

const NOT_FOUND = 'That account is not in this book, or it has been removed.'
const NO_RUN = 'No scoring run has completed yet, so there is nothing to show for this account.'
const GENERIC_FAILURE = 'Something went wrong loading this account.'

const CONFIDENCE_ID = 'presentation-confidence'

const railLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const divider: CSSProperties = { height: 2, background: T.color.rule }

type Load =
  | { phase: 'loading' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; payload: PresentationPayload }

function failureMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return GENERIC_FAILURE
  if (error.code === 'account_not_found') return NOT_FOUND
  if (error.code === 'no_completed_run') return NO_RUN
  return error.message
}

export interface PresentationProps {
  accountId: string
  onExit: () => void
}

export function Presentation({ accountId, onExit }: PresentationProps): JSX.Element {
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  /**
   * The configuration in force, for the assumptions line's numbers.
   *
   * Separate from `load` rather than awaited alongside it, and the screen draws
   * without it: the figures are what a rep turned the laptop around for, and
   * making them wait on a second request — or worse, fail with it — would let a
   * sentence of context decide whether the presentation happens. `null` is a
   * designed state, and {@link assumptionsTail} has a true sentence for it.
   */
  const [config, setConfig] = useState<CurrentConfig | null>(null)
  const root = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    void api.config()
      .then((current) => { if (!cancelled) setConfig(current) })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    // `StrictMode` runs this twice in development and both runs are GETs of the
    // same route, so there is nothing to undo — only a second answer to ignore.
    let cancelled = false
    setLoad({ phase: 'loading' })
    void api.presentation(accountId)
      .then((payload) => { if (!cancelled) setLoad({ phase: 'ready', payload }) })
      .catch((error: unknown) => { if (!cancelled) setLoad({ phase: 'failed', message: failureMessage(error) }) })
    return () => { cancelled = true }
  }, [accountId])

  /**
   * The mode, on the document rather than only on this subtree.
   *
   * Two things it buys. The document behind the overlay cannot be scrolled — a
   * stray wheel event over a customer-facing screen must not move anything
   * underneath it, and the overlay does its own scrolling (`overflow: auto`)
   * rather than the body's — and "which mode is this browser in" is answerable
   * from the document itself, which is where a rep's own check and a test both
   * look.
   *
   * It is the *second* guard now, not the only one: `AccountDetail.tsx` puts
   * the drill-down under `display: none` while this screen is open, so there is
   * no flags banner left to scroll into view. Kept because the two fail
   * differently — one is about what exists, the other about what moves — and
   * because the review found this line deletable with a green suite. Both are
   * asserted, positively, in `Presentation.test.tsx`.
   */
  useEffect(() => {
    const { body } = document
    const previousOverflow = body.style.overflow
    body.dataset['mode'] = 'presentation'
    body.style.overflow = 'hidden'
    return () => {
      delete body.dataset['mode']
      body.style.overflow = previousOverflow
    }
  }, [])

  const exit = useCallback(() => { onExit() }, [onExit])

  // Escape leaves. The rep is standing at somebody's desk with the laptop
  // turned around; the way back must not require finding a specific button.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => { if (event.key === 'Escape') exit() }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey) }
  }, [exit])

  // Focus moves into the overlay, so the next Tab lands on `Exit presentation`
  // rather than continuing through the drill-down that is no longer visible.
  useEffect(() => { root.current?.focus() }, [])

  /**
   * **The focus trap `aria-modal="true"` claims.**
   *
   * `role="dialog"` with `aria-modal="true"` is a *claim* that everything
   * outside this element is inert, and the claim is now true on both halves:
   * `AccountDetail.tsx` puts the drill-down under `display: none` while this
   * screen is open, which takes it out of the accessibility tree, out of print
   * and out of the tab order, and this handler wraps Tab and Shift+Tab inside
   * the overlay. Either alone would leave a gap — the wrapper is what makes
   * "outside is inert" true for a screen reader, and the trap is what keeps Tab
   * from leaving for the browser's own chrome.
   *
   * A wrapping trap rather than `inert` on the siblings: `inert` is not
   * implemented by jsdom, so a trap built on it is one no test in this suite
   * can see — the "measuring the environment" failure this campaign has now
   * found roughly a dozen times. This one is asserted, and `Escape` remains the
   * way out, so a keyboard user is never held in a screen with no exit.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return
      const container = root.current
      if (container === null) return
      const items = focusableWithin(container)
      const first = items[0]
      const last = items[items.length - 1]
      if (first === undefined || last === undefined) {
        // Nothing focusable inside yet (the payload is still in flight). Tab
        // must still not leave, so it lands back on the overlay itself.
        event.preventDefault()
        container.focus()
        return
      }
      const active = document.activeElement
      if (active === null || !container.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus()
        return
      }
      if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault()
        last.focus()
        return
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey) }
  }, [])

  const name = load.phase === 'ready' ? load.payload.account.name : ''
  const meta = load.phase === 'ready'
    ? `${load.payload.account.serviceAddress} · Account ${load.payload.account.accountNumber}`
    : ''

  return (
    <div
      ref={root}
      tabIndex={-1}
      data-mode="presentation"
      data-testid="presentation"
      role="dialog"
      aria-modal="true"
      aria-label="Presentation view"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: '#fff',
        color: T.color.ink,
        fontFamily: T.fontFamily,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      <header
        style={{
          padding: '22px 40px',
          borderBottom: `1.5px solid ${T.color.rule}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, minWidth: 0 }}>
          <img src={logoUrl} alt="APS" style={{ width: 120, height: 'auto', display: 'block' }} />
          <div style={{ minWidth: 0 }}>
            <div
              data-testid="presentation-name"
              style={{ fontSize: 26, fontWeight: T.weight.bold, letterSpacing: '-0.015em' }}
            >
              {name}
            </div>
            <div style={{ fontSize: 13, color: T.color.muted, marginTop: 3 }}>{meta}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={exit}
          data-testid="exit-presentation"
          style={{
            border: `1.5px solid ${T.color.border}`,
            background: '#fff',
            color: T.color.ink,
            padding: '10px 14px',
            fontFamily: T.fontFamily,
            fontSize: T.micro.size,
            fontWeight: T.weight.bold,
            letterSpacing: '0.08em',
            textTransform: T.micro.transform,
            borderRadius: T.radius.button,
            cursor: 'pointer',
            flex: 'none',
          }}
        >
          {EXIT_LABEL}
        </button>
      </header>

      {load.phase === 'loading'
        ? <p style={{ margin: 40, fontSize: 14, color: T.color.body }}>Loading…</p>
        : null}
      {load.phase === 'failed'
        ? <p style={{ margin: 40, fontSize: 14, color: T.color.body, maxWidth: '60ch' }}>{load.message}</p>
        : null}
      {load.phase === 'ready'
        ? (
          load.payload.estimate === null
            ? (
              <p data-testid="nothing-to-present" style={{ margin: 40, fontSize: 14, color: T.color.body, maxWidth: '60ch' }}>
                {NOTHING_TO_PRESENT}
              </p>
            )
            : <Body estimate={load.payload.estimate} config={config} />
        )
        : null}
    </div>
  )
}

/**
 * The two columns, `1fr 380px` — the mock's, and `T.rail.presentation` is that
 * 380 rather than a literal.
 */
function Body(
  { estimate, config }: { estimate: PresentationEstimate; config: CurrentConfig | null },
): JSX.Element {
  const { profile, nomination, value } = estimate.result

  return (
    <div
      style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: `1fr ${T.rail.presentation}px`,
        minHeight: 0,
      }}
      data-testid="presentation-body"
    >
      <div
        style={{
          padding: '32px 40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRight: `1.5px solid ${T.color.rule}`,
          minWidth: 0,
        }}
      >
        <div
          data-testid="kicker"
          style={{
            fontSize: 14,
            fontWeight: T.weight.semi,
            letterSpacing: '0.1em',
            textTransform: T.micro.transform,
            color: T.color.micro,
          }}
        >
          {KICKER}
        </div>
        <div style={{ marginTop: 14 }}>
          {/*
            Task 14's chart, `variant="customer"`, unforked. A customer copy of
            it would be two charts that drift, and the one that drifts into
            drawing something internal is always the customer-facing one. The
            variant changes two strings — `YOUR COMMITMENT` for `NOMINATION`,
            and `— WEAKEST HOUR` for ` BINDS` — and nothing else, because
            everything a customer must not see is absent from its props rather
            than hidden by a branch.
          */}
          <ProfileChart
            profile={profile}
            nomination={nomination}
            eventWindow={estimate.eventWindow}
            variant="customer"
          />
        </div>
        <p
          data-testid="binding-sentence"
          style={{ fontSize: 16, lineHeight: 1.5, marginTop: 18, maxWidth: '78ch' }}
        >
          {/*
            The mock's sentence for the ordinary case, and the one-pager's for
            the other one. "Set by the weakest hour you have to sustain" is a
            claim about where the figure came from, and a nomination entered by
            hand did not come from any hour — the same correction Task 14 made
            to the chart caption and Task 17 made to the KPI caption. It is
            reachable here: `overridden` is on the customer payload.
          */}
          {nomination.overridden
            ? (
              <>
                {'This nomination was entered by hand at '}
                <b>{formatKw(nomination.kw)}</b>
                {', rather than read off the profile above.'}
              </>
            )
            : (
              <>
                {'Your commitment is set by the weakest hour you have to sustain — '}
                <b>{`${formatHour(nomination.bindingHour)}, ${formatKw(nomination.kw)}`}</b>
                {' — not the window average. The earlier hours carry more headroom.'}
              </>
            )}
        </p>
      </div>

      <div
        style={{
          padding: 32,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 26,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={railLabel}>{NOMINATION_LABEL}</div>
            {/*
              Display rule 1, at the one figure this whole screen exists to put
              in front of somebody. `KwFigure` will not render a kW number
              without a label — `label="adjacent"` has to name the element that
              carries one — so the rule is a type error here rather than a
              review habit.
            */}
            <span id={CONFIDENCE_ID}>
              <ConfidencePill confidence={estimate.confidence} wording="full" />
            </span>
          </div>
          <div style={{ marginTop: 10 }}>
            <KwFigure
              kw={nomination.kw}
              confidence={estimate.confidence}
              size="presentation"
              tone="navy"
              label="adjacent"
              confidenceLabelId={CONFIDENCE_ID}
              testId="nomination-kw"
            />
          </div>
        </div>

        <div style={divider} />

        <div>
          <div style={railLabel}>{SEASON_LABEL}</div>
          <div
            data-testid="season-total"
            style={{
              fontSize: 52,
              fontWeight: T.weight.bold,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              marginTop: 10,
              fontVariantNumeric: 'tabular-nums',
              color: T.color.money,
            }}
          >
            {formatMoney(value.seasonTotal)}
          </div>
          {/*
            Split, and the split is the point (R-11): the total alone hides
            which half a change moves. `seasonDemand` is the capacity payment
            and `seasonEnergy` the energy payment; neither is computed here.
          */}
          <dl style={{ margin: 0, fontSize: 15 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0 0',
                borderBottom: `1px solid ${T.color.hairline}`,
              }}
            >
              <dt>{CAPACITY_LABEL}</dt>
              <dd data-testid="season-capacity" style={{ margin: 0, fontWeight: T.weight.bold, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(value.seasonDemand)}
              </dd>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0' }}>
              <dt>{ENERGY_LABEL}</dt>
              <dd data-testid="season-energy" style={{ margin: 0, fontWeight: T.weight.bold, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(value.seasonEnergy)}
              </dd>
            </div>
          </dl>
        </div>

        <div style={divider} />

        <p data-testid="assumptions" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: T.color.body }}>
          {`Event window ${formatHour(estimate.eventWindow.startHour)}–`
            + `${formatHour(estimate.eventWindow.endHour)}, and only those hours count. `
            + assumptionsTail(config)}
        </p>
        <p data-testid="disclaimer" style={{ margin: 0, fontSize: 12.5, color: T.color.muted }}>
          {DISCLAIMER}
        </p>
      </div>
    </div>
  )
}
